import React, {
  useEffect,
  useCallback,
  useState,
  useMemo,
  useContext,
} from 'react';

import { useTranslation } from 'react-i18next';
import { Input, Select, Button } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';

import Table from '@components/Table';
import { dateFormat, getDateInISOFormat } from '@utils/formatters';
import StatusMenu from '@components/StatusMenu';
import SelectCustomers from '@components/SelectCustomers';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import moment from 'moment';
import SessionContext from '@providers/Session/SessionContext';
import useSearchParams from '@hooks/useSearchParams';
import CustomTag from '@components/CustomTag';
import useSwr from '@hooks/useSwr';
import DevicesModal from './DevicesModal';

const { Search } = Input;

const getStatus = (t: any) => [
  { label: t('status.PENDING'), value: 'PENDING' },
  { label: t('status.PROCESSING'), value: 'PROCESSING' },
  { label: t('status.COMPLETED'), value: 'COMPLETED' },
];

const Operations = () => {
  const { t } = useTranslation('operational');
  const { hasCustomersRoles } = useContext(SessionContext);

  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [protocolValue, setProtocolValue] = useState<any>();
  const [createdByValue, setCreatedByValue] = useState<any>();
  const [operationId, setOperationId] = useState<string | undefined>();
  const [operationTypeValue, setOperationTypeValue] = useState<
    string | undefined
  >();

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['operationType'],
    extraParams: [
      'customerId',
      'status',
      'protocol',
      'createdBy',
      'startDate',
      'endDate',
    ],
  });

  const {
    customerId,
    status,
    protocol,
    createdBy,
    startDate,
    endDate,
    operationType,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  const { data } = useSwr('/service-proxy/broker/operations', {
    params: {
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      customerId,
      status: status && status.toUpperCase(),
      protocol,
      operationType: operationType && operationType.toUpperCase(),
      createdBy,
      startDate,
      endDate,
    },
  });

  useEffect(() => {
    setProtocolValue(protocol);
  }, [protocol]);

  useEffect(() => {
    setCreatedByValue(createdBy);
  }, [createdBy]);

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
      },
      {
        title: t('table.operationType'),
        dataIndex: 'operationType',
        render: (value: string) => t(`types.${value}`),
      },
      {
        title: t('table.protocol'),
        dataIndex: 'protocol',
      },
      {
        title: t('table.customerName'),
        dataIndex: 'customerName',
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        render: dateFormat,
      },
      {
        title: t('table.createdBy'),
        dataIndex: 'createdBy',
      },
      {
        title: t('table.updatedAt'),
        dataIndex: 'updatedAt',
        render: dateFormat,
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        render: (value: string) => t(`status.${value}`),
      },
      {
        title: t('table.nbDevices'),
        dataIndex: 'nbDevices',
        align: 'center',
        render: (value: string, record: any) => (
          <div>
            <CustomTag>{value}</CustomTag>
            {value && (
              <Button
                icon={<FileSearchOutlined />}
                type="link"
                onClick={() => {
                  setShowDevicesModal(true);
                  setOperationId(record.id);
                  setOperationTypeValue(record.operationType);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    [t],
  );

  const handleDateChange = useCallback(
    (values: any) => {
      const start = getDateInISOFormat(values.startDate);
      const end = getDateInISOFormat(values.endDate);
      setSearchParams({ startDate: start, endDate: end });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: hasCustomersRoles,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('table.customerName'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('table.createdAt'),
        element: (
          <QuickRangePicker
            onChange={handleDateChange}
            defaultValue={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            style={{ width: '100%' }}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('table.createdBy'),
        element: (
          <Search
            onSearch={value => setSearchParams({ createdBy: value })}
            onChange={({ target: { value } }) => setCreatedByValue(value)}
            value={createdByValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('table.protocol'),
        element: (
          <Search
            onSearch={value => setSearchParams({ protocol: value })}
            onChange={({ target: { value } }) => setProtocolValue(value)}
            value={protocolValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 12,
          xl: 8,
          xs: 24,
        },
        label: t('table.status'),
        element: (
          <Select
            optionFilterProp="label"
            options={getStatus(t).map(statusOption => ({
              label: statusOption.label,
              value: statusOption.value,
            }))}
            style={{ width: '100%' }}
            // @ts-ignore
            value={status?.toUpperCase()}
            allowClear
            showSearch
            onChange={value => {
              setSearchParams({ status: value?.toLowerCase() });
            }}
          />
        ),
      },
    ],
    [
      hasCustomersRoles,
      t,
      customerId,
      handleDateChange,
      startDate,
      endDate,
      createdByValue,
      protocolValue,
      status,
      setSearchParams,
    ],
  );

  const statusMenuOperations = useMemo(
    () => [
      {
        label: t('types.SUSPENSION'),
        value: 'suspension',
      },
      {
        label: t('types.SUSPENSION_REVERSE'),
        value: 'suspension_reverse',
      },
      {
        label: t('types.CANCELLATION'),
        value: 'cancellation',
      },
      {
        label: t('types.SUBSTITUTION'),
        value: 'substitution',
      },
      {
        label: t('types.BAIT_RENEWAL'),
        value: 'BAIT_RENEWAL',
      },
    ],
    [t],
  );

  const handleCloseModal = useCallback(() => {
    setShowDevicesModal(false);
  }, []);

  return (
    <div>
      <StatusMenu
        itemActive={operationType}
        data={statusMenuOperations}
        handleSelectedCard={item => {
          setSearchParams({
            operationType:
              item.value !== operationType ? item.value : undefined,
          });
        }}
        style={{ marginBottom: 16 }}
        showIconClear
      />
      <Table
        // @ts-ignore
        columns={columns}
        loading={!data?.content}
        title={t('title')}
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
        tableKey="operational"
        data={data?.content ?? []}
        onTableChange={setSearchParams}
      />
      <DevicesModal
        visible={showDevicesModal}
        onClose={handleCloseModal}
        operationId={operationId}
        operationType={operationTypeValue}
      />
    </div>
  );
};

export default Operations;
